import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useToast } from "@/components/ui/use-toast";
import { Building2, Check, Info, Loader2, Mail, User } from "lucide-react";
import { useEffect, useState } from "react";
import { v4 as uuidv4 } from "uuid";
import {
  InvalidOperationError,
  useOrganization,
} from "../gen/rbt/cloud/v1alpha1/organization/organization_rbt_react";
import { PaymentForm } from "./PaymentForm";

type FlowType = "selection" | "joining" | "creating";

// Generate a random 4-character alphanumeric string.
function generateRandomSuffix(): string {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < 4; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}

// Generate a solo developer org name from email.
function generateSoloOrgName(email: string): string {
  const username = email.split("@")[0] || "developer";
  // Clean up the username: replace dots/underscores with
  // spaces, capitalize.
  const cleanName = username
    .replace(/[._]/g, " ")
    .split(" ")
    .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
    .join("");
  return `${cleanName}-${generateRandomSuffix()}`;
}

// "Get Started" screen where the user picks how they want
// to use Reboot Cloud.
function FlowSelection({
  userEmail,
  onSelect,
}: {
  userEmail: string;
  onSelect: (flow: FlowType, initialName?: string) => void;
}) {
  return (
    <div className="min-h-screen bg-background">
      <div className="w-screen min-w-96 flex justify-center">
        <div className="max-w-2xl w-full mx-10 py-8">
          <h1 className="text-3xl font-bold mb-2">Get Started</h1>
          <p className="text-muted-foreground mb-6">
            Choose how you'd like to use Reboot Cloud.
          </p>
          <Alert className="mb-6">
            <Info className="h-4 w-4" />
            <AlertTitle>New to Reboot?</AlertTitle>
            <AlertDescription>
              <a
                href="https://docs.reboot.dev"
                target="_blank"
                rel="noopener noreferrer"
                className="font-medium underline"
              >
                Learn how to get started
              </a>{" "}
              before deploying to Reboot Cloud.
            </AlertDescription>
          </Alert>
          <div className="space-y-4">
            <Card
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() =>
                onSelect("creating", generateSoloOrgName(userEmail))
              }
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <User className="h-6 w-6 text-primary" />
                  <div>
                    <CardTitle>I'm a solo developer</CardTitle>
                    <CardDescription>
                      Get started quickly with a personal organization. We'll
                      set up everything for you.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>

            <Card
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => onSelect("joining")}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Mail className="h-6 w-6 text-primary" />
                  <div>
                    <CardTitle>I'm joining an organization</CardTitle>
                    <CardDescription>
                      Join an existing team that's already using Reboot Cloud.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>

            <Card
              className="cursor-pointer hover:border-primary transition-colors"
              onClick={() => onSelect("creating")}
            >
              <CardHeader>
                <div className="flex items-center gap-3">
                  <Building2 className="h-6 w-6 text-primary" />
                  <div>
                    <CardTitle>I'm creating an organization</CardTitle>
                    <CardDescription>
                      Set up a new organization for your team or company.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
}

// Informational screen for users who want to join an
// existing organization.
function JoinOrganization() {
  return (
    <div className="min-h-screen bg-background">
      <div className="w-screen min-w-96 flex justify-center">
        <div className="max-w-2xl w-full mx-10 py-8">
          <h1 className="text-3xl font-bold mb-2">Join an Organization</h1>
          <p className="text-muted-foreground mb-6">
            Here's how to join an existing organization on Reboot Cloud.
          </p>
          <Card>
            <CardHeader>
              <CardTitle>Request an invitation</CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">
                  To join an existing organization, you'll need an invitation
                  from the organization owner or an administrator.
                </p>
                <ol className="list-decimal list-inside space-y-2 text-sm">
                  <li>
                    Contact your organization owner and ask them to send you an
                    invitation.
                  </li>
                  <li>
                    They can invite you from the organization settings page by
                    entering your email address.
                  </li>
                  <li>
                    You'll receive an email with an invitation link. Click the
                    link to join the organization.
                  </li>
                </ol>
              </div>
              <Alert>
                <Info className="h-4 w-4" />
                <AlertTitle>Don't have an invitation yet?</AlertTitle>
                <AlertDescription>
                  If you haven't received an invitation, reach out to your
                  organization owner. They'll need to add your email address to
                  send you an invite.
                </AlertDescription>
              </Alert>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}

// Non-interactive preview of the payment form, shown before
// the org is created so the user can see what comes next.
function PaymentFormPreview({
  onSuccess,
  onError,
}: {
  onSuccess: (setupIntentId: string) => void;
  onError: (message: string) => void;
}) {
  return (
    <div className="opacity-50 pointer-events-none">
      <PaymentForm
        trialEligible
        titlePrefix="Step 2: "
        onSuccess={onSuccess}
        onError={onError}
      />
    </div>
  );
}

// Interactive payment form. Mounts only after the org is
// created, so the `useSubscription` hook targets an
// existing org.
function PaymentFormView({
  organizationId,
  onSuccess,
  onError,
  onBillingExempt,
}: {
  organizationId: string;
  onSuccess: (setupIntentId: string) => void;
  onError: (message: string) => void;
  onBillingExempt: () => void;
}) {
  const { useSubscription } = useOrganization({
    id: organizationId,
  });
  const { response } = useSubscription();
  const clientSecret = response?.subscription?.clientSecret;
  const paymentMethodId = response?.subscription?.paymentMethodId;
  const billingExempt = response?.billingExempt ?? false;

  // Stripe has confirmed the card but the webhook hasn't
  // saved it to backend state yet.
  const [cardConfirmed, setCardConfirmed] = useState(false);
  const waitingForPaymentMethod = cardConfirmed && !paymentMethodId;

  // Navigate away once the backend has saved the card, or as soon as
  // the organization is marked billing-exempt (the reactive read
  // means this can happen while the user is looking at the payment
  // form).
  useEffect(() => {
    if (billingExempt) {
      onBillingExempt();
    } else if (cardConfirmed && paymentMethodId) {
      onSuccess(paymentMethodId);
    }
  }, [
    billingExempt,
    cardConfirmed,
    paymentMethodId,
    onSuccess,
    onBillingExempt,
  ]);

  if (billingExempt) {
    // The effect above navigates away.
    return <></>;
  }

  return (
    <div className={!clientSecret ? "opacity-50 pointer-events-none" : ""}>
      <PaymentForm
        clientSecret={clientSecret}
        trialEligible
        titlePrefix="Step 2: "
        onSuccess={() => setCardConfirmed(true)}
        onError={onError}
        isSubmitting={waitingForPaymentMethod}
      />
    </div>
  );
}

interface CreateOrganizationProps {
  onOrganizationCreated: (organizationId: string) => void;
  userEmail?: string;
  // When set, the org already exists. Skip org creation and
  // go straight to the payment form.
  existingOrganizationId?: string;
  existingOrganizationName?: string;
  // Override the initial flow. Defaults to "selection".
  initialFlow?: FlowType;
}

export function CreateOrganization({
  onOrganizationCreated,
  userEmail = "",
  existingOrganizationId,
  existingOrganizationName,
  initialFlow,
}: CreateOrganizationProps) {
  const isResuming = existingOrganizationId !== undefined;
  const [flow, setFlow] = useState<FlowType>(
    initialFlow ?? (isResuming ? "creating" : "selection")
  );
  const [organizationName, setOrganizationName] = useState(
    existingOrganizationName ?? ""
  );
  const [isSaving, setIsSaving] = useState(false);
  const [orgCreated, setOrgCreated] = useState(isResuming);
  const [organizationId, setOrganizationId] = useState(
    existingOrganizationId ?? uuidv4()
  );
  const { toast } = useToast();
  const { create } = useOrganization({
    id: organizationId,
  });

  const handleFlowSelect = (selected: FlowType, initialName?: string) => {
    if (initialName) {
      setOrganizationName(initialName);
    }
    setFlow(selected);
  };

  const handleCreateOrganization = async () => {
    if (!organizationName.trim()) {
      toast({
        title: "Error",
        description: "Please enter an organization name.",
        variant: "destructive",
      });
      return;
    }

    setIsSaving(true);

    try {
      const { aborted } = await create({
        organizationName: organizationName.trim(),
        subscribe: true,
      });

      if (aborted) {
        const reason =
          aborted.error instanceof InvalidOperationError
            ? aborted.error.reason
            : undefined;
        toast({
          title: "Error",
          description: reason || "Failed to create organization.",
          variant: "destructive",
        });
        return;
      }

      setOrgCreated(true);
      toast({
        title: "Organization created",
        description: `"${organizationName}" has been created.`,
      });
    } catch (error) {
      toast({
        title: "Error",
        description:
          error instanceof Error
            ? error.message
            : "An unexpected error occurred.",
        variant: "destructive",
      });
    } finally {
      setIsSaving(false);
    }
  };

  const handlePaymentSuccess = async (_setupIntentId: string) => {
    toast({
      title: "Card saved",
      description: `Payment method saved for ${organizationName}.`,
    });

    onOrganizationCreated(organizationId);

    // Reset for potential next organization creation.
    setOrganizationId(uuidv4());
  };

  const handlePaymentError = (message: string) => {
    toast({
      title: "Payment Error",
      description: message,
      variant: "destructive",
    });
  };

  const handleBillingExempt = () => {
    toast({
      title: "Organization is billed internally",
      description: `No payment method is required for ${organizationName}.`,
    });

    onOrganizationCreated(organizationId);

    // Reset for potential next organization creation.
    setOrganizationId(uuidv4());
  };

  if (flow === "selection") {
    return <FlowSelection userEmail={userEmail} onSelect={handleFlowSelect} />;
  }

  if (flow === "joining") {
    return <JoinOrganization />;
  }

  // Organization creation + payment form.
  const title = isResuming
    ? "Complete your organization setup"
    : "Set up your organization";
  const subtitle = isResuming
    ? "Add a payment method to finish setting up your " + "organization."
    : "Create your organization, then add a payment method. " +
      "You will only be charged for the applications you " +
      "bring up.";

  return (
    <div className="min-h-screen bg-background">
      <div className="w-screen min-w-96 flex justify-center">
        <div className="max-w-2xl w-full mx-10 py-8">
          <h1 className="text-3xl font-bold mb-2">{title}</h1>
          <p className="text-muted-foreground mb-6">{subtitle}</p>
          <div className="space-y-6">
            <Card>
              <CardHeader>
                <CardTitle>
                  Step 1: Choose a name for your organization
                </CardTitle>
                <CardDescription>
                  This will be the name you pass to{" "}
                  <code className="font-mono text-primary">
                    rbt cloud up --organization="Acme Corp" ...
                  </code>{" "}
                  when you bring up your application.
                </CardDescription>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="orgName">Organization name</Label>
                    <Input
                      id="orgName"
                      placeholder="e.g. Acme Corp"
                      value={organizationName}
                      onChange={(e) => setOrganizationName(e.target.value)}
                      required
                      disabled={isSaving || orgCreated}
                    />
                  </div>
                  {orgCreated ? (
                    <Button disabled variant="outline" className="w-full">
                      <Check className="h-4 w-4 mr-2" />
                      Saved
                    </Button>
                  ) : (
                    <Button
                      className="w-full"
                      onClick={handleCreateOrganization}
                      disabled={isSaving || !organizationName.trim()}
                    >
                      {isSaving ? (
                        <>
                          <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                          Saving...
                        </>
                      ) : (
                        "Create organization"
                      )}
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
            {orgCreated ? (
              <PaymentFormView
                organizationId={organizationId}
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
                onBillingExempt={handleBillingExempt}
              />
            ) : (
              <PaymentFormPreview
                onSuccess={handlePaymentSuccess}
                onError={handlePaymentError}
              />
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
